import React, { useState } from "react";
import { FilterValue, FilterConfig, FilterActions, DataViewMode, DataViewOption } from "@/components/cards/widgets/TableFilter";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useCardFilters } from "./useCardFilters";
import CardGrid from "./CardGrid";
import FolderGroupSection from "./FolderGroupSection";
import GridControls from "./GridControls";
import EmptyState from "./EmptyState";
import { FolderGroupedTableDisplay } from "@/components/table-main/FolderGroupedTableDisplay";
import { TemplateGroupedTableDisplay } from "@/components/table-main/TemplateGroupedTableDisplay";
import { PendingChangesToolbar } from "@/components/table-main/PendingChangesToolbar";
import { createCustomDataPackage, downloadZipBlob, type PackageResult } from "@/lib/integrations/libraryProcessorCustom";
import { processLibraryItems, groupItemsByBlueprint } from "@/lib/integrations/libraryProcessorShared";
import { ExportTemplatesConfig, NamingConvention, ExportTemplate } from "@/types/exportTemplates";
import { getDatabasePresetForGame } from "@/lib/integrations/exportTemplatePresets";
import { FlowCanvas } from "@/components/flow/FlowCanvas";
import { FlowConfig } from "@/components/flow/types";
import { useTypesDataContext } from "@/providers/TypesDataProvider";
import { useTagsDataContext } from "@/providers/TagsDataProvider";
import { getFolderPath } from "@/lib/stores/folderStore";
import { fetchDocumentItems } from "@/lib/stores/documentStore";
import { apiService } from "@/lib/api-functions/api-service";
import { useGlossaryBlueprintsDataContext } from "@/providers/GlossaryBlueprintsDataProvider";
import { useToast } from "@/hooks/use-toast";
export interface CardData {
  id: string;
  gameId?: string;
  title: string;
  owner?: string;
  ownerId?: string; // User ID of the owner
  editPermissions?: 'Open' | 'Locked'; // Edit permissions: Open (anyone can edit) or Locked (only owner)
  tags: string[];
  description?: string;
  lastUpdate?: string;
  version?: number | string;
  icon?: React.ReactNode;
  iconBg?: string;
  shadowType?: "black" | "gradient" | "none" | "color" | "status" | "template";
  shadowColor?: string;
  statusColor?: string; // Color from status workflow
  hasVersionMismatch?: boolean;
  status?: string; // Changed from 'draft' | 'published' to string to support custom statuses
  onDelete?: (id: string, gameId: string) => void;
  onEdit?: (id: string) => void;
  [key: string]:
  | React.ReactNode
  | ((id: string, gameId?: string) => void)
  | string
  | string[]
  | boolean
  | undefined;
}

export interface FolderGroup {
  folderId: string | null;
  folderName: string;
  cards: CardData[];
}

export interface GridLayoutProps {
  title?: string;
  cards: CardData[];
  groupedCards?: FolderGroup[];
  onCardClick?: (card: CardData) => void;
  onDelete?: (id: string, gameId: string) => void;
  onEdit?: (id: string) => void;
  className?: string;
  style?: React.CSSProperties;
  renderCard?: (card: CardData) => React.ReactNode;
  showHeader?: boolean;
  requiredTags?: string[];
  selectedFolderId?: string | null;
  filterConfig?: FilterConfig[];
  filterActions?: Partial<FilterActions>;
  itemType?: string;
  // Data view mode props
  availableSections?: DataViewOption[];
  availableFields?: DataViewOption[];
  // Raw item data for section/field view transformations
  rawItems?: any[];
  // Template lookup for grouping by template
  templateLookup?: Record<string, { id: string; name: string; color?: string }>;
  // Callback when a cell value changes in table view
  onCellValueChange?: (rowKey: string, cellKey: string, newValue: any) => void;
  // Pending changes props
  pendingChangesCount?: number;
  hasPendingChanges?: boolean;
  modifiedItems?: Set<string>;
  modifiedCells?: Map<string, string[]>;
  onSaveItem?: (itemId: string) => void;
  onDiscardItem?: (itemId: string) => void;
  onSaveAllItems?: () => Promise<{ success: boolean; conflicts: any[] }>;
  onDiscardAllItems?: () => void;
  onForceSaveItem?: (itemId: string) => Promise<boolean>;
  // Folder path lookup for displaying folder paths instead of IDs
  folderPathLookup?: (folderId: string | null | undefined) => string;
  // Flow view props
  flowConfig?: FlowConfig<any>; // Flow configuration for visualizing items
  selectedItemForFlow?: any; // Selected item to visualize in flow view
  allowFlowView?: boolean; // Whether to enable flow view toggle
  // Bulk delete handler for selected items
  onDeleteSelected?: (ids: string[]) => Promise<void>;
}

export default function GridLayout({
  title,
  cards,
  groupedCards,
  onCardClick,
  onDelete,
  onEdit,
  className = "",
  style = {},
  renderCard,
  showHeader = false,
  requiredTags = [],
  selectedFolderId,
  filterConfig,
  filterActions,
  itemType,
  availableSections = [],
  availableFields = [],
  rawItems = [],
  templateLookup = {},
  onCellValueChange,
  // Pending changes props
  pendingChangesCount = 0,
  hasPendingChanges = false,
  modifiedItems = new Set<string>(),
  modifiedCells = new Map<string, string[]>(),
  onSaveItem,
  onDiscardItem,
  onSaveAllItems,
  onDiscardAllItems,
  onForceSaveItem,
  folderPathLookup,
  // Flow view props
  flowConfig,
  selectedItemForFlow,
  allowFlowView = false,
  // Bulk delete
  onDeleteSelected,
}: GridLayoutProps) {
  const { t } = usePlatformTranslation();
  const { selectedGame } = useSimplifiedUserData();
  const { types } = useTypesDataContext();
  const { tags } = useTagsDataContext();
  const { glossaryBlueprints } = useGlossaryBlueprintsDataContext();
  const { toast } = useToast();
  const [gridSize, setGridSize] = useState<"1" | "2" | "3" | "4" | "5" | "6">("6");

  // Initialize viewMode from localStorage
  const [viewMode, setViewMode] = useState<"grid" | "table" | "flow">(() => {
    const saved = localStorage.getItem('locanest-view-mode');
    return (saved === 'grid' || saved === 'table' || saved === 'flow') ? saved as "grid" | "table" | "flow" : 'grid';
  });

  // Data view mode state - persist per itemType
  const dataViewModeKey = itemType ? `locanest-data-view-mode-${itemType}` : 'locanest-data-view-mode';
  const [dataViewMode, setDataViewMode] = useState<DataViewMode>(() => {
    const saved = localStorage.getItem(dataViewModeKey);
    return (saved === 'metadata' || saved === 'sections' || saved === 'fields') ? saved as DataViewMode : 'metadata';
  });
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  // Lifted column selection state from FolderGroupedTableDisplay to persist across data updates
  // Persisted to localStorage per itemType to survive page refreshes and data updates
  const columnSelectionsKey = itemType ? `locanest-column-selections-${itemType}` : 'locanest-column-selections';
  const [groupColumnSelections, setGroupColumnSelections] = useState<Record<string, string[]>>(() => {
    if (!columnSelectionsKey) return {};
    try {
      const saved = localStorage.getItem(columnSelectionsKey);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Add transition state for smooth switching
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayMode, setDisplayMode] = useState(viewMode);
  // Initialize as true if viewMode comes from localStorage
  const hasRenderedRef = React.useRef(viewMode === localStorage.getItem('locanest-view-mode'));

  const gridContainerRef = React.useRef<HTMLDivElement>(null);
  const isAutoAdjustingRef = React.useRef(false);

  // Track selected rows across all table groups for export functionality
  const [selectedRowsByGroup, setSelectedRowsByGroup] = useState<Record<string, Set<string>>>({});

  // Save viewMode to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem('locanest-view-mode', viewMode);
  }, [viewMode]);

  // Save dataViewMode to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem(dataViewModeKey, dataViewMode);
  }, [dataViewModeKey, dataViewMode]);

  // Save groupColumnSelections to localStorage whenever it changes
  React.useEffect(() => {
    if (columnSelectionsKey) {
      localStorage.setItem(columnSelectionsKey, JSON.stringify(groupColumnSelections));
    }
  }, [columnSelectionsKey, groupColumnSelections]);

  // Mark as rendered after first paint
  React.useEffect(() => {
    hasRenderedRef.current = true;
  }, []);

  // Handle smooth view mode transitions (skip on first render)
  React.useEffect(() => {
    if (viewMode !== displayMode && hasRenderedRef.current) {
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setDisplayMode(viewMode);
        setIsTransitioning(false);
      }, 150); // Half of the fade duration
      return () => clearTimeout(timer);
    }
  }, [viewMode, displayMode]);

  // Use custom hook for all filter logic - pass itemType for filter persistence
  const {
    searchTerm,
    setSearchTerm,
    activeFilters,
    setActiveFilters,
    shadowMode,
    setShadowMode,
    filteredCards,
  } = useCardFilters({
    cards,
    requiredTags,
    selectedFolderId,
    filterConfig,
    itemType,
  });

  // Automatically calculate optimal grid size based on container width
  React.useEffect(() => {
    if (!gridContainerRef.current) return;

    let debounceTimer: NodeJS.Timeout;

    const calculateOptimalGridSize = () => {
      const container = gridContainerRef.current;
      if (!container) return;

      const containerWidth = container.clientWidth;
      if (containerWidth === 0) return;

      // Minimum card size is 250px (since cards are square, this ensures both width and height >= 250px)
      const minCardSize = 250;
      // Gap between cards is 24px (gap-6 = 1.5rem)
      const gap = 24;

      // Calculate how many tiles can fit: floor((containerWidth + gap) / (minCardSize + gap))
      // We add gap to containerWidth because there's no gap after the last item
      const maxTiles = Math.floor((containerWidth + gap) / (minCardSize + gap));

      // Clamp between 1 and 6 tiles
      const optimalTiles = Math.max(1, Math.min(6, maxTiles));

      const targetSize = String(optimalTiles) as "1" | "2" | "3" | "4" | "5" | "6";

      if (targetSize !== gridSize && !isAutoAdjustingRef.current) {
        isAutoAdjustingRef.current = true;
        setGridSize(targetSize);
        setTimeout(() => { isAutoAdjustingRef.current = false; }, 200);
      }
    };

    // Initial calculation with delay to ensure DOM is ready
    const initialTimer = setTimeout(calculateOptimalGridSize, 150);

    // Monitor for size changes using ResizeObserver
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(calculateOptimalGridSize, 100);
    });

    // Observe the container
    resizeObserver.observe(gridContainerRef.current);

    // Add window resize listener for additional responsiveness
    const handleWindowResize = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(calculateOptimalGridSize, 150);
    };
    window.addEventListener('resize', handleWindowResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
      clearTimeout(initialTimer);
      clearTimeout(debounceTimer);
    };
  }, [gridSize, filteredCards.length]); // Re-run when grid size or card count changes

  const clearFilters = () => {
    setSearchTerm("");
    setActiveFilters([]);
  };

  // Export functionality - downloads objects using the configured export template
  const handleExportData = React.useCallback(async (exportMode: 'single' | 'multiple') => {
    if (!selectedGame) {
      console.warn("No game selected for export");
      toast({
        title: "No Game Selected",
        description: "Please select a game before exporting.",
        variant: "destructive",
      });
      return;
    }

    let itemsToExport: any[] = [];

    // In table mode, export only selected items
    if (viewMode === "table") {
      // Collect all selected row IDs from all groups
      const allSelectedIds = new Set<string>();
      Object.values(selectedRowsByGroup).forEach(groupSet => {
        groupSet.forEach(id => allSelectedIds.add(id));
      });

      // If no items selected, show a user-facing warning
      if (allSelectedIds.size === 0) {
        console.warn("No items selected for export");
        toast({
          title: "No Items Selected",
          description: "Please select items in the table to export. Use the checkboxes to select items.",
          variant: "destructive",
        });
        return;
      }

      // Filter rawItems to only include selected items
      itemsToExport = rawItems.filter(item => allSelectedIds.has(item.id));

      console.log(`Exporting ${itemsToExport.length} selected items from table view`);
    } else {
      // In grid mode, export all items in the project
      itemsToExport = rawItems;
      console.log(`Exporting all ${itemsToExport.length} items from grid view`);
    }

    // If no items to export, show warning
    if (itemsToExport.length === 0) {
      console.warn("No items to export");
      toast({
        title: "No Items to Export",
        description: "There are no items available to export.",
        variant: "destructive",
      });
      return;
    }

    // Get the export template - either from settings or use game's default preset
    const exportConfig = selectedGame?.settings?.exportTemplates as ExportTemplatesConfig | undefined;
    let templateToUse: ExportTemplate | undefined;

    // First, try to get the active template from settings
    if (exportConfig?.activeTemplateId) {
      templateToUse = exportConfig.templates?.find(t => t.id === exportConfig.activeTemplateId);
    }

    // If no active template in settings, get the default preset based on game's engine/platform
    if (!templateToUse) {
      templateToUse = getDatabasePresetForGame(selectedGame);
      console.log(`No active template configured, using default preset: ${templateToUse?.name} for ${selectedGame.settings?.engine || 'unknown'} engine`);
    }

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const exportType = viewMode === "table" ? "selected" : "all";

    // Use the template for database export (Roblox or Unity)
    if (templateToUse) {
      try {
        // Get the category mapping from the template (maps template names to category names)
        const categoryMapping = (templateToUse as any).categoryMapping as Record<string, string> | undefined;

        // Filter items to only include those whose template is in the categoryMapping
        // This ensures we only export items that have been configured in DatabaseSection
        let filteredItemsToExport = itemsToExport;

        if (categoryMapping && Object.keys(categoryMapping).length > 0) {
          // Get template names that are mapped
          const mappedTemplateNames = new Set(Object.keys(categoryMapping));

          // Filter items - only keep items whose blueprint name is in the mapping
          filteredItemsToExport = itemsToExport.filter(item => {
            // Find the blueprint for this item
            const blueprint = glossaryBlueprints?.find(bp => bp.id === item.blueprint_id);
            if (!blueprint) return false;

            // Check if this blueprint's name is in the category mapping
            return mappedTemplateNames.has(blueprint.name);
          });

          console.log(`Filtered items: ${filteredItemsToExport.length}/${itemsToExport.length} items match category mappings`);
        }

        // If no items after filtering, show warning
        if (filteredItemsToExport.length === 0) {
          console.warn("No items match the configured category mappings");
          return;
        }

        // Get item IDs for processing
        const itemIds = filteredItemsToExport.map(item => item.id);

        // Process items using the same method as settings page
        const processedItems = processLibraryItems(itemIds, selectedGame.id);

        if (processedItems.length === 0) {
          console.warn("No processed items for export");
          return;
        }

        // Group by blueprint
        const blueprintGroups = groupItemsByBlueprint(processedItems);

        // Create folder path resolver for folder-based exports
        const folderPathResolver = (folderId: string | null | undefined): string => {
          if (!folderId) return '';
          return getFolderPath(selectedGame.id, 'library', folderId);
        };

        // Get naming convention from game settings (default to 'original')
        const namingConvention: NamingConvention = 'original';

        // Fetch dialogs for export
        let dialogs: any[] = [];
        try {
          const dialogsResult = await apiService.fetchDialogs(selectedGame.id);
          if (dialogsResult.success && dialogsResult.data) {
            dialogs = dialogsResult.data;
            console.log(`📝 Fetched ${dialogs.length} dialogs for export`);
          }
        } catch (error) {
          console.warn('Failed to fetch dialogs, continuing without them:', error);
        }

        // Fetch documents for export (needed for document field references)
        let documents: any[] = [];
        try {
          documents = await fetchDocumentItems(selectedGame.id);
          console.log(`📄 Fetched ${documents.length} documents for export`);
        } catch (error) {
          console.warn('Failed to fetch documents, continuing without them:', error);
        }

        // Create complete data package using the same method as settings page
        const packageResult = await createCustomDataPackage(
          blueprintGroups,
          selectedGame.id,
          selectedGame.name,
          templateToUse,
          glossaryBlueprints,
          types,
          tags,
          itemsToExport, // Pass filtered items, not all rawItems
          folderPathResolver,
          namingConvention,
          templateToUse.databaseSettings?.namespace || 'Grimoire.Data',
          templateToUse.databaseSettings?.className || 'Database',
          selectedGame.settings, // Pass game settings for languages
          dialogs,
          documents
        );

        // Download the package (ZIP or single file)
        if (packageResult.isZip) {
          downloadZipBlob(packageResult.blob, packageResult.filename);
        } else {
          // Download single file directly
          const link = document.createElement('a');
          link.href = URL.createObjectURL(packageResult.blob);
          link.download = packageResult.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);
        }

        const exportedCount = filteredItemsToExport.length;
        console.log(`Exported ${exportedCount} items using ${templateToUse.name} as ${packageResult.filename}`);

        toast({
          title: "Export Successful",
          description: `Exported ${exportedCount} ${viewMode === "table" ? "selected" : ""} item${exportedCount !== 1 ? "s" : ""} as ${packageResult.filename}`,
        });

        return;
      } catch (error) {
        console.error('Error exporting with template:', error);
        // Fall back to JSON export
      }
    }

    // Fallback to raw JSON export if no template or template export failed
    if (exportMode === 'single') {
      // Export all items in a single file
      const jsonData = JSON.stringify(itemsToExport, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      const filename = `${itemType || 'items'}-${exportType}-${timestamp}.json`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log(`Exported ${itemsToExport.length} items in a single file`);

      toast({
        title: "Export Successful",
        description: `Exported ${itemsToExport.length} ${viewMode === "table" ? "selected" : ""} item${itemsToExport.length !== 1 ? "s" : ""} as ${filename}`,
      });
    } else {
      // Export each item as a separate file
      itemsToExport.forEach((item, index) => {
        const jsonData = JSON.stringify(item, null, 2);
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        // Use item name or ID for filename, with index to ensure uniqueness
        const itemName = item.name || item.title || item.id || `item-${index}`;
        const safeItemName = itemName.replace(/[^a-z0-9_-]/gi, '_');
        link.download = `${itemType || 'item'}-${safeItemName}-${timestamp}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Add small delay between downloads to prevent browser blocking
        if (index < itemsToExport.length - 1) {
          // Use a synchronous delay for simplicity
          const start = Date.now();
          while (Date.now() - start < 50) {
            // 50ms delay between downloads
          }
        }
      });

      console.log(`Exported ${itemsToExport.length} items as separate files`);

      toast({
        title: "Export Successful",
        description: `Exported ${itemsToExport.length} ${viewMode === "table" ? "selected" : ""} item${itemsToExport.length !== 1 ? "s" : ""} as separate JSON files`,
      });
    }
  }, [viewMode, selectedRowsByGroup, rawItems, itemType, selectedGame, templateLookup, glossaryBlueprints, types, tags, toast]);

  // Create grouped cards from filtered cards for table view
  const filteredGroupedCards = React.useMemo(() => {

    if (!groupedCards) {
      // If no groupedCards provided (single folder view), create a single group
      if (filteredCards.length > 0) {
        return [{
          folderId: null,
          folderName: "Items",
          cards: filteredCards
        }];
      }
      return undefined;
    }

    // Create a Set of filtered card IDs for quick lookup
    const filteredCardIds = new Set(filteredCards.map(card => card.id));

    // Filter each group's cards
    const result = groupedCards
      .map(group => ({
        ...group,
        cards: group.cards.filter(card => filteredCardIds.has(card.id))
      }))
      .filter(group => group.cards.length > 0); // Remove empty groups

    return result;
  }, [groupedCards, filteredCards]);

  // Create template-grouped cards for sections/fields view
  const templateGroupedCards = React.useMemo(() => {
    if (dataViewMode === "metadata") return undefined;

    // Group filtered cards by blueprint_id (template)
    const templateGroups: Record<string, { templateId: string; templateName: string; templateColor?: string; cards: CardData[] }> = {};
    const noTemplateCards: CardData[] = [];

    filteredCards.forEach(card => {
      const blueprintId = (card as any).blueprint_id;
      if (blueprintId && templateLookup[blueprintId]) {
        const template = templateLookup[blueprintId];
        if (!templateGroups[blueprintId]) {
          templateGroups[blueprintId] = {
            templateId: blueprintId,
            templateName: template.name,
            templateColor: template.color,
            cards: []
          };
        }
        templateGroups[blueprintId].cards.push(card);
      } else {
        noTemplateCards.push(card);
      }
    });

    const result = Object.values(templateGroups).sort((a, b) =>
      a.templateName.localeCompare(b.templateName)
    );

    // Add items without template as a separate group
    if (noTemplateCards.length > 0) {
      result.push({
        templateId: 'no-template',
        templateName: 'No Template',
        templateColor: undefined,
        cards: noTemplateCards
      });
    }

    return result;
  }, [dataViewMode, filteredCards, templateLookup]);

  // Check if data view mode selector should be shown (only for library and templates)
  const showDataViewMode = itemType === 'library' || itemType === 'blueprints';

  // Check if database export template is active (Roblox or Unity)
  // This is always true now since we use database presets by default based on game engine
  const isDatabaseExportActive = React.useMemo(() => {
    if (!selectedGame) return false;

    const exportConfig = selectedGame?.settings?.exportTemplates as ExportTemplatesConfig | undefined;
    const activeId = exportConfig?.activeTemplateId;

    // If there's an active template configured, check if it's a database template
    if (activeId) {
      return activeId === 'preset-roblox-database' || activeId === 'preset-unity-static-typed' || activeId === 'preset-roblox-static-typed' || activeId === 'preset-unity-runtime-database' || activeId === 'preset-unity-id-records';
    }

    // If no active template, we'll use the default database preset based on game engine
    // The default presets (Roblox Database, Unity Static Typed) are always database exports
    return true;
  }, [selectedGame]);

  // Calculate selected rows count and IDs for bulk operations
  const selectedRowsInfo = React.useMemo(() => {
    const allSelectedIds: string[] = [];
    Object.values(selectedRowsByGroup).forEach(groupSet => {
      groupSet.forEach(id => allSelectedIds.push(id));
    });
    return {
      hasSelectedRows: allSelectedIds.length > 0,
      selectedIds: allSelectedIds,
      count: allSelectedIds.length,
    };
  }, [selectedRowsByGroup]);

  // Handle bulk delete of selected items
  const handleDeleteSelected = React.useCallback(async () => {
    if (!onDeleteSelected || selectedRowsInfo.selectedIds.length === 0) return;

    await onDeleteSelected(selectedRowsInfo.selectedIds);

    // Clear selection after delete
    setSelectedRowsByGroup({});
  }, [onDeleteSelected, selectedRowsInfo.selectedIds]);

  // Merge export handler into filterActions
  const mergedFilterActions: Partial<FilterActions> = React.useMemo(() => {
    return {
      ...filterActions,
      onExportData: handleExportData,
      directExport: isDatabaseExportActive, // Skip dropdown menu if using database export template
      // Add delete functionality when in table view (always show, enabled based on selection)
      ...(onDeleteSelected && viewMode === 'table' && {
        onDelete: handleDeleteSelected,
        hasSelectedRows: selectedRowsInfo.hasSelectedRows,
      }),
    };
  }, [filterActions, handleExportData, isDatabaseExportActive, onDeleteSelected, viewMode, handleDeleteSelected, selectedRowsInfo.hasSelectedRows]);

  return (
<div className={`space-y-6 flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-4 pr-4 ${className}`} style={style} >      {/* Unified Filter Controls - Sticky */}
      {filterConfig && (
        <div className="sticky top-0 z-40 bg-surface py-2 -mx-6 px-6">
          <GridControls
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filterConfig={filterConfig}
            activeFilters={activeFilters}
            onFiltersChange={setActiveFilters}
            filterActions={mergedFilterActions}
            shadowMode={shadowMode}
            onShadowModeChange={setShadowMode}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            allowFlowView={allowFlowView}
            dataViewMode={showDataViewMode ? dataViewMode : undefined}
            onDataViewModeChange={showDataViewMode ? setDataViewMode : undefined}
          />
        </div>
      )}

      {/* Pending Changes Toolbar - only show in table view when there are unsaved changes */}
      {displayMode === "table" && hasPendingChanges && onSaveAllItems && onDiscardAllItems && onForceSaveItem && onDiscardItem && (
        <PendingChangesToolbar
          pendingCount={pendingChangesCount}
          hasPendingChanges={hasPendingChanges}
          onSaveAll={onSaveAllItems}
          onDiscardAll={onDiscardAllItems}
          onForceSaveItem={onForceSaveItem}
          onDiscardItem={onDiscardItem}
        />
      )}

      {/* Cards Display with smooth transitions */}
      <div
        className="relative transition-opacity duration-300 ease-in-out"
        style={{ opacity: isTransitioning ? 0 : 1 }}
      >
        {filteredCards.length === 0 ? (
          <EmptyState
            hasCards={cards.length > 0}
            onClearFilters={searchTerm || activeFilters.length > 0 ? clearFilters : undefined}
          />
        ) : (
          <>
            {/* Table View - Folder Grouped (Metadata mode) */}
            {displayMode === "table" && dataViewMode === "metadata" && filteredGroupedCards && filteredGroupedCards.length > 0 && itemType && (
              <div key="table-view-folder">
                <FolderGroupedTableDisplay
                  groupedCards={filteredGroupedCards}
                  itemType={itemType}
                  selectedFolderId={selectedFolderId}
                  onCardClick={onCardClick}
                  rawItems={rawItems}
                  templateLookup={templateLookup}
                  onCellValueChange={onCellValueChange}
                  modifiedItems={modifiedItems}
                  modifiedCells={modifiedCells}
                  onSaveItem={onSaveItem}
                  onDiscardItem={onDiscardItem}
                  folderPathLookup={folderPathLookup}
                  shadowMode={shadowMode}
                  groupColumnSelections={groupColumnSelections}
                  onGroupColumnSelectionsChange={setGroupColumnSelections}
                  selectedRowsByGroup={selectedRowsByGroup}
                  onSelectedRowsChange={setSelectedRowsByGroup}
                />
              </div>
            )}

            {/* Table View - Template Grouped (Sections/Fields mode) */}
            {displayMode === "table" && dataViewMode !== "metadata" && templateGroupedCards && templateGroupedCards.length > 0 && itemType && (
              <div key="table-view-template">
                <TemplateGroupedTableDisplay
                  templateGroups={templateGroupedCards}
                  itemType={itemType}
                  dataViewMode={dataViewMode}
                  selectedSections={selectedSections}
                  selectedFields={selectedFields}
                  availableSections={availableSections}
                  availableFields={availableFields}
                  rawItems={rawItems}
                  onCardClick={onCardClick}
                  templateLookup={templateLookup}
                  onCellValueChange={onCellValueChange}
                  shadowMode={shadowMode}
                  selectedRowsByGroup={selectedRowsByGroup}
                  onSelectedRowsChange={setSelectedRowsByGroup}
                />
              </div>
            )}

            {/* Grid View - Grouped */}
            {displayMode === "grid" && groupedCards && (
              <div
                ref={gridContainerRef}
                key={`grouped-${gridSize}`}
              >
                <FolderGroupSection
                  groupedCards={groupedCards}
                  gridSize={gridSize}
                  shadowMode={shadowMode}
                  searchTerm={searchTerm}
                  activeFilters={activeFilters}
                  filterConfig={filterConfig}
                  onCardClick={onCardClick}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  renderCard={renderCard}
                />
              </div>
            )}

            {/* Grid View - Ungrouped */}
            {displayMode === "grid" && !groupedCards && (
              <div
                ref={gridContainerRef}
                key={`cards-${gridSize}`}
              >
                <CardGrid
                  cards={filteredCards}
                  gridSize={gridSize}
                  shadowMode={shadowMode}
                  onCardClick={onCardClick}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  renderCard={renderCard}
                />
              </div>
            )}

            {/* Flow View */}
            {displayMode === "flow" && (
              <div key="flow-view" className="h-[calc(100vh-200px)] min-h-[600px] border rounded-lg">
                {flowConfig && selectedItemForFlow ? (
                  <FlowCanvas
                    initialNodes={flowConfig.dataToNodes(selectedItemForFlow)}
                    initialEdges={flowConfig.dataToEdges(selectedItemForFlow)}
                    nodeTypes={flowConfig.nodeTypes}
                    edgeTypes={flowConfig.edgeTypes}
                    showControls={true}
                    showMiniMap={true}
                    showBackground={true}
                    showDetailsPanel={false}
                    fitViewOnLoad={true}
                    validateConnection={flowConfig.validateConnection}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <p className="text-lg text-muted-foreground mb-2">Flow View</p>
                      <p className="text-sm text-muted-foreground">
                        {flowConfig
                          ? "Select an item from the list to visualize its flow"
                          : "Flow visualization is not configured for this item type"
                        }
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
